import { useRef, useEffect } from 'react'
import { X, AlertCircle, CheckCircle, Info, AlertTriangle, Play } from 'lucide-react'
import './EtlExecutionLog.css'

/**
 * Format an ISO timestamp into HH:MM:SS.mmm for the log display.
 */
function formatTime(timestamp) {
  if (!timestamp) return ''
  try {
    const d = new Date(timestamp)
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    const ss = String(d.getSeconds()).padStart(2, '0')
    const ms = String(d.getMilliseconds()).padStart(3, '0')
    return `${hh}:${mm}:${ss}.${ms}`
  } catch {
    return ''
  }
}

/**
 * Return the appropriate Lucide icon for a log level.
 */
function getIcon(level) {
  switch (level) {
    case 'error':
      return <AlertCircle size={12} />
    case 'warning':
      return <AlertTriangle size={12} />
    case 'complete':
      return <CheckCircle size={12} />
    case 'start':
      return <Play size={12} />
    case 'info':
    default:
      return <Info size={12} />
  }
}

function EtlExecutionLog({ logs, onClear }) {
  const scrollRef = useRef(null)

  // Auto-scroll to bottom when new entries are added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [logs])

  return (
    <div className="etl-execution-log">
      <div className="etl-log-toolbar">
        <span className="etl-log-count">{logs.length} entries</span>
        <button onClick={onClear} disabled={logs.length === 0}>
          <X size={12} /> Clear
        </button>
      </div>
      <div className="etl-log-entries" ref={scrollRef}>
        {logs.length === 0 ? (
          <div className="etl-log-empty">No log entries</div>
        ) : (
          logs.map((log, i) => (
            <div
              key={log.id || i}
              className={`etl-log-entry etl-log-${log.level || 'info'}`}
            >
              {getIcon(log.level)}
              <span className="etl-log-time">
                {formatTime(log.timestamp)}
              </span>
              {log.source && (
                <span className="etl-log-source">[{log.source}]</span>
              )}
              <span className="etl-log-message">{log.message}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default EtlExecutionLog
